/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Box,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Typography,
  makeStyles,
} from "@material-ui/core";
import {
  InfoBox,
  InfoLine,
} from "../components-molecules";
import { SubscriptionDetailHeader } from "../components-molecules/SubscriptionDetailHeader";
import { ITheme, colours } from "../constants/theme";
import { useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { getMerchantSubscription, MerchantSubscription, ShopSubscription, setMerchantPayAsYouGo, getMerchantShopSubscriptionsList, setMerchantPremiumFee } from "../redux/actions/subscriptions";
import { useParams } from "react-router-dom";
import { textButtonHoverAndActive } from "../utils/styles/buttonStyles";
import { SubscriptionPayAsYouGoModal } from "../components-molecules/SubscriptionPayAsYouGo";
import { SubscriptionPremiumFeeModal } from "../components-molecules/SubscriptionPremiumFee";
import { useAppWebSocket } from "../hooks/useAppWebSocket";
import { DataUpdateSocketMessage } from "./Subscriptions";
import SubscriptionCardShop from "../components-molecules/SubscriptionCardShop";
import { SubscriptionCardModal } from "../utils/subscriptions";
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: ITheme) => ({
  container: {
    display: "flex",
    justifyContent: "center",
  },
  root: {
    maxWidth: "840px",
    paddingLeft: 0,
    paddingRight: 0,
  },
  detailsContainer: {
    marginTop: "24px",
    paddingLeft: 0,
    paddingRight: 0,
  },
  stores: {
    marginTop: 16,
  },
  storeList: {
    // InfoLine offset
  },
  storeContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    padding: '16px 0',
  },
  storeInfoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  storeImage: {
    borderRadius: 4,
    border: `1px solid ${colours.dark12}`,
  },
  active: {
    color: theme.palette.success.main,
    fontWeight: 700,
  },
  suspended: {
    color: theme.palette.warning.main,
    fontWeight: 700,
  },
  cancelButton: {
    padding: '12px 16px',
    borderRadius: '8px',
    color: theme.palette.primary.main,
    ...textButtonHoverAndActive
  },
  creditLeft: {
    fontWeight: 'normal',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
    '& > svg': {
      marginBottom: 3
    }
  },
  shopContainer: {
    padding: "18px 24px 0",
    border: "1px solid rgba(0, 0, 0, 0.08)",
    boxSizing: "border-box",
    borderRadius: "20px",
    marginBottom: 24,
  }
}));

export function SubscriptionDetailMerchant() {
  const { t } = useTranslation();
  const params: any = useParams();
  const userData = useSelector((state: any) => state.login);
  const isAdmin = useMemo(() => userData.role === "Admin", [userData]);
  const classes = useStyles();

  const [subscription, setSubscription] = useState<MerchantSubscription>();
  const [storeSubscriptions, setStoreSubscriptions] = useState<ShopSubscription[]>([]);
  const [visibleModal, setVisibleModal] = useState<SubscriptionCardModal>();
  const [storesLoading, setStoresLoading] = useState(false);

  const [total, setTotal] = useState(0);

  const [waitingForResponse, setWaitingForResponse] = useState(false);

  const [listLastUpdate, setListLastUpdate] = useState<string>();
  const { lastJsonMessage } = useAppWebSocket<DataUpdateSocketMessage | null>(
    '/ws/data-update',
    {}
  );

  useEffect(() => {
    if (lastJsonMessage && lastJsonMessage.LastUpdate !== listLastUpdate) {
      setListLastUpdate(lastJsonMessage.LastUpdate);
      getMerchantData();
      getMerchantShops();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastJsonMessage, lastJsonMessage?.LastUpdate, lastJsonMessage?.UpdateType]);

  const getMerchantData = () => {
    getMerchantSubscription(
      isAdmin,
      params.id,
      (res) => { setSubscription(res); },
      (err) => { /* Removed console.log */ },
    );
  };

  const setPayAsYouGo = (payg: number) => {
    if (subscription) {
      setMerchantPayAsYouGo(
        { MerchantId: subscription.MerchantId, Price: Math.round(payg * 100) },
        (res) => {
          getMerchantData();
          setWaitingForResponse(false);
          setVisibleModal(undefined);
        },
        (err) => { /* Removed console.log */ }
      );
    }
  };

  const setPremiumFee = (pf: number) => {
    if (subscription) {
      setMerchantPremiumFee(
        { MerchantId: subscription.MerchantId, Fee: Math.round(pf * 100) },
        (res) => {
          getMerchantData();
          setWaitingForResponse(false);
          setVisibleModal(undefined);
        },
        (err) => { /* Removed console.log */ }
      );
    }
  };

  const getMerchantShops = (currentPage = 0) => {
    if (subscription) {
      getMerchantShopSubscriptionsList(
        isAdmin,
        subscription.MerchantId,
        {
          Page: 0,
          Size: 5000,
          Query: ""
        },
        (res) => {
          if (currentPage === 0) setTotal(res.Total);
          setStoreSubscriptions(res.Subscriptions);
          setStoresLoading(false);
        },
        (err) => { /* Removed console.log */ },
      );
    }
  };

  useEffect(() => {
    getMerchantShops();
  }, [subscription]);

  useEffect(() => {
    getMerchantData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  return (
    <Box className={classes.container}>
      {subscription &&
        <Container className={classes.root}>
          <SubscriptionDetailHeader
            name={subscription?.MerchantName}
type="Merchant"
            status={subscription ? subscription?.HasActiveSubscription ? t('merchant.active') : t('merchant.inactive') : undefined}
            MerchantId={subscription?.MerchantId}
            isAdmin={isAdmin}
          />
          <Container className={classes.detailsContainer}>
            <InfoBox title={t('merchant.subscriptionInformation')}>
              <InfoLine
                clickable
name={t('merchant.payAsYouGoPrice')}
value={subscription?.PayAsYouGoPrice ? `${t('currencySymbol')}${(subscription.PayAsYouGoPrice / 100).toFixed(2)}` : '-'}
                onClick={() => !isAdmin ? undefined : setVisibleModal(SubscriptionCardModal.PAY_AS_YOU_GO)}>
                {isAdmin && <InfoLine.ArrowArea></InfoLine.ArrowArea>}
              </InfoLine>
              <InfoLine
                clickable
name={t('merchant.conciergeFee')}
value={subscription?.PremiumFee ? `${t('currencySymbol')}${(subscription.PremiumFee / 100).toFixed(2)}` : '-'}
                onClick={() => !isAdmin ? undefined : setVisibleModal(SubscriptionCardModal.PREMIUM_FEE)}>
                {isAdmin && <InfoLine.ArrowArea></InfoLine.ArrowArea>}
              </InfoLine>
            </InfoBox>
            {storesLoading
              ? <Grid
                item
                container
                justifyContent="center"
                alignItems="center"
                style={{ marginTop: "96px" }}
              >
                <CircularProgress />
              </Grid>
              :
              <Grid container className={classes.stores}>
                <Grid item xs id="scrollableTarget" className={classes.storeList}>
                  {storeSubscriptions.length === 0
                    ? <Container style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                      <CircularProgress />
                    </Container>
                    :
                    <Box className={classes.shopContainer}>
                      <Box style={{ marginBottom: 16 }}>
                        <Typography variant="h6" component="span" >
                          Shops
                        </Typography>
                      </Box>
                      {storeSubscriptions.map((s, i) =>
                        <Box key={i}>
                          <SubscriptionCardShop shop={s} classes={classes} merchant={subscription} isAdmin={isAdmin} />
                          {
                            storeSubscriptions.length - 1 !== i &&
                            <Divider style={{ background: colours.dark08 }} />
                          }
                        </Box>
                      )}
                    </Box>
                  }
                </Grid>
              </Grid>
            }
          </Container>
        </Container>
      }

      <SubscriptionPayAsYouGoModal
        classes={classes}
        isVisible={visibleModal === SubscriptionCardModal.PAY_AS_YOU_GO}
        isDisabled={waitingForResponse}
        close={() => setVisibleModal(undefined)}
        setPayAsYouGo={setPayAsYouGo}
        defaultValue={subscription?.PayAsYouGoPrice ? (subscription?.PayAsYouGoPrice / 100).toFixed(2) : null}
      />
      <SubscriptionPremiumFeeModal
        classes={classes}
        isVisible={visibleModal === SubscriptionCardModal.PREMIUM_FEE}
        isDisabled={waitingForResponse}
        close={() => setVisibleModal(undefined)}
        setPremiumFee={setPremiumFee}
        defaultValue={subscription?.PremiumFee ? (subscription?.PremiumFee / 100).toFixed(2) : null}
      />
    </Box>
  );
};
